import { patientIntakeProvenanceOf } from "@emr/contracts";
import { type ReactNode, useId } from "react";
import type {
  AllergyItem,
  FamilyHistorySection,
  ImmunizationItem,
  MedicationItem,
  PatientDemographics,
  PmhItem,
  SocialHistoryItem,
} from "../../api/clinicalWorkspace";
import {
  MedicationChangeButton,
  MedicationRefillButton,
  MedicationRefillCheckbox,
  MedicationRefillSelectedButton,
  useMedicationRefill,
} from "../../components/MedicationRefill";
import { formatCalendarDate } from "../schedule/scheduleTime";
import { ageFromDateOfBirth } from "./chartFacts";
import type { ProfileSection } from "./profileSections";

/**
 * Read-only bodies for the profile sections. Read mode is prose, chips and
 * definition lists — never a form. The per-section editors (next slice) mount
 * inside the same frame with one Edit action, so nothing here changes shape
 * when editing arrives.
 */

export function SectionPanel({
  section,
  actions,
  children,
}: {
  readonly section: ProfileSection;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  const titleId = useId();
  return (
    <section className="pp-panel" aria-labelledby={titleId}>
      <header className="pp-panel-head">
        <span className="pp-panel-icon" aria-hidden="true">
          {section.icon()}
        </span>
        <div className="pp-panel-title">
          <h2 id={titleId}>{section.label}</h2>
          <p>{section.description}</p>
        </div>
        {actions ? <div className="pp-panel-actions">{actions}</div> : null}
      </header>
      <div className="pp-panel-body">{children}</div>
    </section>
  );
}

export function EmptyLine({ children }: { readonly children: ReactNode }) {
  return <p className="pp-empty">{children}</p>;
}

export function PlaceholderBody({ section }: { readonly section: ProfileSection }) {
  return (
    <div className="pp-placeholder">
      <p>{section.description}</p>
      <p className="pp-quiet">Nothing is filed here yet.</p>
    </div>
  );
}

/**
 * "Patient intake · Oct 8, 2026" on an allergy, medication or history entry applied from the
 * patient's pre-visit questionnaire (PA-6): the date the patient submitted it, on the clinic's
 * calendar. It takes the place of the "Patient-reported" source chip, so an entry never shows
 * both.
 */
export function IntakeProvenanceChip({ item }: { readonly item: unknown }) {
  const provenance = patientIntakeProvenanceOf(item);
  if (provenance === undefined) return null;
  return (
    <span className="pp-chip provenance">
      Patient intake · {formatCalendarDate(provenance.submittedOn)}
    </span>
  );
}

/* ---- Demographics ------------------------------------------------------- */

function formatAddress(address: PatientDemographics["address"]): string {
  if (address === undefined) return "";
  return [
    address.line1,
    address.line2,
    address.city,
    address.region ?? address.province ?? address.state,
    address.postalCode,
    address.country,
  ]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(", ");
}

const SEX_LABELS: Readonly<Record<string, string>> = {
  female: "Female",
  male: "Male",
  other: "Other",
  unknown: "Not recorded",
};

export function DemographicsBody({ demographics }: { readonly demographics: PatientDemographics }) {
  const age = ageFromDateOfBirth(demographics.dateOfBirth);
  const address = formatAddress(demographics.address);
  const contact = demographics.emergencyContact;
  const contactLine = contact
    ? [contact.name, contact.relationship, contact.phone]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
        .join(" · ")
    : "";
  const coverage = demographics.coverage;
  return (
    <dl className="pp-dl">
      <div>
        <dt>Legal name</dt>
        <dd>
          {[demographics.legalFirstName, demographics.legalMiddleName, demographics.legalLastName]
            .filter((part) => part !== undefined && part.trim().length > 0)
            .join(" ")}
        </dd>
      </div>
      {demographics.preferredName ? (
        <div>
          <dt>Preferred name</dt>
          <dd>{demographics.preferredName}</dd>
        </div>
      ) : null}
      <div>
        <dt>Date of birth</dt>
        <dd>
          {demographics.dateOfBirth}
          {age !== undefined ? ` · ${age} years` : ""}
        </dd>
      </div>
      <div>
        <dt>Sex</dt>
        <dd>{SEX_LABELS[demographics.administrativeSex] ?? demographics.administrativeSex}</dd>
      </div>
      <div>
        <dt>MRN</dt>
        <dd className="pp-mono">{demographics.mrn}</dd>
      </div>
      <div>
        <dt>Health number</dt>
        <dd className="pp-mono">{demographics.healthNumber ?? "Not on file"}</dd>
      </div>
      {coverage?.versionCode ? (
        <div>
          <dt>Version code</dt>
          <dd className="pp-mono">{coverage.versionCode}</dd>
        </div>
      ) : null}
      {coverage?.expiryDate ? (
        <div>
          <dt>Card expiry date</dt>
          <dd className="pp-mono">{coverage.expiryDate}</dd>
        </div>
      ) : null}
      <div>
        <dt>Coverage</dt>
        <dd>
          {coverage
            ? `${coverage.payerName} · ${coverage.memberId}${
                coverage.status === "pending_verification" ? " · pending verification" : ""
              }`
            : "Cash / none on file"}
        </dd>
      </div>
      <div>
        <dt>Phone</dt>
        <dd>{demographics.phone ?? "Not on file"}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{demographics.email ?? "Not on file"}</dd>
      </div>
      <div>
        <dt>Address</dt>
        <dd>{address.length > 0 ? address : "Not on file"}</dd>
      </div>
      <div>
        <dt>Emergency contact</dt>
        <dd>{contactLine.length > 0 ? contactLine : "Not on file"}</dd>
      </div>
    </dl>
  );
}

/* ---- Past medical history ---------------------------------------------- */

const PMH_STATUS_ORDER: Readonly<Record<PmhItem["status"], number>> = {
  active: 0,
  resolved: 1,
  inactive: 2,
};

export function PmhBody({ items }: { readonly items: readonly PmhItem[] }) {
  const visible = items
    .filter((item) => item.status !== "inactive")
    .sort((a, b) => PMH_STATUS_ORDER[a.status] - PMH_STATUS_ORDER[b.status]);
  if (visible.length === 0) return <EmptyLine>Nothing recorded yet.</EmptyLine>;
  return (
    <ul className="pp-list">
      {visible.map((item) => (
        <li key={item.id} className={item.status === "resolved" ? "resolved" : ""}>
          <span className="pp-list-main">
            <span className="pp-list-title">{item.display}</span>
            {item.category === "surgical" ? <span className="pp-chip">Surgical</span> : null}
            {item.status === "resolved" ? <span className="pp-chip quiet">Resolved</span> : null}
            {item.code ? <span className="pp-mono pp-code">{item.code}</span> : null}
            <IntakeProvenanceChip item={item} />
          </span>
          <span className="pp-list-meta">
            {item.onsetDate ? `Since ${item.onsetDate.slice(0, 4)}` : "Onset not recorded"}
            {item.note ? ` · ${item.note}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ---- Family history ----------------------------------------------------- */

export function FamilyHistoryBody({ section }: { readonly section: FamilyHistorySection }) {
  const rows = section.rows.filter((row) => row.status === "active");
  const narrative = section.narrative.trim();
  if (narrative.length === 0 && rows.length === 0) {
    return <EmptyLine>Nothing recorded yet.</EmptyLine>;
  }
  return (
    <div className="pp-stack">
      {narrative.length > 0 ? <p className="pp-narrative">{narrative}</p> : null}
      {rows.length > 0 ? (
        <ul className="pp-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span className="pp-list-main">
                <span className="pp-list-title">{row.display}</span>
                <span className="pp-chip quiet">{row.relation.replace(/_/g, " ")}</span>
              </span>
              <span className="pp-list-meta">
                {row.onsetAge !== undefined ? `From age ${row.onsetAge}` : ""}
                {row.deceased ? (row.onsetAge !== undefined ? " · deceased" : "Deceased") : ""}
                {row.note ? ` · ${row.note}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ---- Social history (sparse: only captured kinds render) --------------- */

const SOCIAL_KIND_LABELS: Readonly<Record<SocialHistoryItem["kind"], string>> = {
  tobacco: "Tobacco",
  alcohol: "Alcohol",
  cannabis: "Cannabis",
  substance: "Substances",
  occupation: "Occupation",
  housing: "Housing",
  exercise: "Exercise",
  diet: "Diet",
  other: "Other",
};

export function SocialHistoryBody({ items }: { readonly items: readonly SocialHistoryItem[] }) {
  const visible = items.filter((item) => item.clinicalStatus === "active");
  if (visible.length === 0) return <EmptyLine>Nothing recorded yet.</EmptyLine>;
  return (
    <ul className="pp-tags">
      {visible.map((item) => (
        <li key={item.id}>
          <span className="pp-tag-kind">{SOCIAL_KIND_LABELS[item.kind]}</span>
          <span className="pp-tag-body">
            {item.detail}
            {item.quantity ? ` · ${item.quantity}` : ""}
            {item.frequency ? ` · ${item.frequency}` : ""}
          </span>
          <span className="pp-chip quiet">{item.status}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---- Allergies ---------------------------------------------------------- */

export function AllergiesBody({ items }: { readonly items: readonly AllergyItem[] }) {
  const visible = items.filter((item) => item.status !== "inactive");
  if (visible.length === 0)
    return <EmptyLine>No allergy entries recorded. Status not confirmed.</EmptyLine>;
  return (
    <ul className="pp-list">
      {visible.map((item) => (
        <li key={item.id} className={item.status === "resolved" ? "resolved" : ""}>
          <span className="pp-list-main">
            <span className="pp-list-title">{item.display}</span>
            {item.severity && item.severity !== "unknown" ? (
              <span className={item.severity === "severe" ? "pp-chip alert" : "pp-chip"}>
                {item.severity}
              </span>
            ) : null}
            {item.status === "resolved" ? <span className="pp-chip quiet">Resolved</span> : null}
            <IntakeProvenanceChip item={item} />
          </span>
          <span className="pp-list-meta">{item.reaction ?? "Reaction not recorded"}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---- Immunizations ------------------------------------------------------ */

export function ImmunizationsBody({ items }: { readonly items: readonly ImmunizationItem[] }) {
  const visible = items
    .filter((item) => item.status !== "entered_in_error")
    .sort((a, b) => b.administeredOn.localeCompare(a.administeredOn));
  if (visible.length === 0) return <EmptyLine>Nothing recorded yet.</EmptyLine>;
  return (
    <ul className="pp-list">
      {visible.map((item) => (
        <li key={item.id}>
          <span className="pp-list-main">
            <span className="pp-list-title">{item.display}</span>
            <span className="pp-mono pp-code">{item.administeredOn}</span>
            {item.status === "not_done" ? <span className="pp-chip quiet">Not done</span> : null}
          </span>
          <span className="pp-list-meta">
            {[
              item.doseNumber !== undefined ? `Dose ${item.doseNumber}` : undefined,
              item.route,
              item.site,
              item.lotNumber ? `Lot ${item.lotNumber}` : undefined,
              item.note,
            ]
              .filter((part): part is string => typeof part === "string" && part.length > 0)
              .join(" · ") || "Details not recorded"}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ---- Medications -------------------------------------------------------- */

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  clinician_prescribed: "Prescribed here",
  clinician_asserted: "Recorded here",
  patient_reported: "Patient-reported",
  external_document: "Imported",
  medication_reconciliation: "Reconciled",
  laboratory_result: "From a lab result",
};

function MedicationRow({ item }: { readonly item: MedicationItem }) {
  // A physician refills an active medication from here (EXECDEV-101).
  const refillable = useMedicationRefill() !== null && item.status === "active";
  return (
    <li className={item.status === "active" ? "" : "resolved"}>
      <span className="pp-list-main">
        {refillable ? (
          <MedicationRefillCheckbox medicationId={item.id} display={item.display} />
        ) : null}
        <span className="pp-list-title">{item.display}</span>
        {patientIntakeProvenanceOf(item) !== undefined ? (
          <IntakeProvenanceChip item={item} />
        ) : item.sourceKind ? (
          <span className="pp-chip quiet">{SOURCE_LABELS[item.sourceKind] ?? item.sourceKind}</span>
        ) : null}
        {refillable ? (
          <>
            <span className="pp-list-spacer" />
            <MedicationChangeButton
              medicationId={item.id}
              display={item.display}
              sourceKind={item.sourceKind}
              variant="profile"
            />
            <MedicationRefillButton
              medicationId={item.id}
              display={item.display}
              variant="profile"
            />
          </>
        ) : null}
      </span>
      <span className="pp-list-meta">{item.sig ?? "Directions not recorded"}</span>
    </li>
  );
}

export function MedicationsBody({ items }: { readonly items: readonly MedicationItem[] }) {
  const refill = useMedicationRefill();
  const active = items.filter((item) => item.status === "active");
  const stopped = items.filter((item) => item.status !== "active");
  if (active.length === 0 && stopped.length === 0) {
    return <EmptyLine>No medications recorded.</EmptyLine>;
  }
  return (
    <div className="pp-stack">
      {refill === null ? (
        <p className="pp-subhead">Active</p>
      ) : (
        <div className="pp-refill-head">
          <p className="pp-subhead">Active</p>
          <MedicationRefillSelectedButton
            listedIds={active.map((item) => item.id)}
            variant="profile"
          />
        </div>
      )}
      {active.length === 0 ? (
        <EmptyLine>No active medications.</EmptyLine>
      ) : (
        <ul className="pp-list">
          {active.map((item) => (
            <MedicationRow key={item.id} item={item} />
          ))}
        </ul>
      )}
      {stopped.length > 0 ? (
        <>
          <p className="pp-subhead">Stopped</p>
          <ul className="pp-list">
            {stopped.map((item) => (
              <MedicationRow key={item.id} item={item} />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
